import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoConference,
  useRoomContext,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Video, Loader2, Clock, Globe, AlertCircle } from 'lucide-react';
import api, { API_URL } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { MeetingRecordingControls } from '../components/MeetingRecordingControls';
import { RecordingConsentBanner } from '../components/RecordingConsentBanner';
import { createLocalVideoTrack } from 'livekit-client';
import BackgroundSelector from '../components/meeting/BackgroundSelector';
import { useBackgroundProcessor } from '../hooks/useBackgroundProcessor';
import InCallBackgroundToggle from '../components/meeting/InCallBackgroundToggle';
import WaitingRoom from '../components/WaitingRoom';
import WaitingRoomPanel from '../components/WaitingRoomPanel';

// Try to import CustomVideoConference — if not directly exported, we'll use a simpler layout
import { useTranslation } from '../i18n';
let VideoRoom;
try {
  // The CustomLiveKitCall exports JoinCallButton which contains the room internally
  // For meetings, we need to render the room directly
  VideoRoom = null; // Will use LiveKitRoom directly
} catch (e) {
  VideoRoom = null;
}

export default function JoinMeetingPage() {
  const { t } = useTranslation();
  const { joinCode } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [meetingInfo, setMeetingInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState(null);

  // Guest form
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  // Call state
  const [connectionData, setConnectionData] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  const [joining, setJoining] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState('idle');
  const [waitingId, setWaitingId] = useState(null);

  // Virtual background state
  const [selectedBg, setSelectedBg] = useState(null);
  const [previewTrack, setPreviewTrack] = useState(null);
  const { applyBackground, getSavedSelection } = useBackgroundProcessor();

  // Fetch meeting info on load
  useEffect(() => {
    async function fetchInfo() {
      try {
        const resp = await fetch(`${API_URL}/api/meetings/join/${joinCode}`);
        if (!resp.ok) {
          const data = await resp.json();
          setError(data.detail || 'Meeting not found');
          return;
        }
        const data = await resp.json();
        setMeetingInfo(data);
      } catch (err) {
        setError('Unable to load meeting information');
      } finally {
        setLoadingInfo(false);
      }
    }
    fetchInfo();
  }, [joinCode]);

  // Camera preview for pre-join lobby (with saved background restore)
  useEffect(() => {
    if (isInCall) return;
    let track;
    let cancelled = false;
    (async () => {
      try {
        track = await createLocalVideoTrack({ resolution: { width: 640, height: 480 } });
        if (cancelled) { track.stop(); return; }
        setPreviewTrack(track);
        const saved = getSavedSelection();
        if (saved) {
          setSelectedBg(saved);
          await applyBackground(track, saved);
        }
      } catch (err) {
        console.warn('Camera preview unavailable:', err.message);
      }
    })();
    return () => {
      cancelled = true;
      if (track) {
        track.stop();
        setPreviewTrack(null);
      }
    };
  }, [isInCall]);

  const handleBgSelect = async (bg) => {
    setSelectedBg(bg);
    if (previewTrack) await applyBackground(previewTrack, bg);
  };

  // Join as authenticated user
  const joinAsUser = async () => {
    setJoining(true);
    try {
      const data = await api.post(`/meetings/join/${joinCode}/token`);
      if (data.waitingRoom) {
        setWaitingId(data.waitingId);
        sessionStorage.setItem('pf_waitingId', data.waitingId);
        if (previewTrack) { previewTrack.stop(); setPreviewTrack(null); }
      } else {
        setConnectionData(data);
        if (previewTrack) { previewTrack.stop(); setPreviewTrack(null); }
        setIsInCall(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to join meeting');
    } finally {
      setJoining(false);
    }
  };

  // Join as guest
  const joinAsGuest = async () => {
    if (!guestName.trim()) {
      setError('Please enter your name');
      return;
    }
    setJoining(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/meetings/join/${joinCode}/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: guestName.trim(), email: guestEmail.trim() }),
      });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to join');
      }
      const data = await resp.json();
      if (data.waitingRoom) {
        setWaitingId(data.waitingId);
        sessionStorage.setItem('pf_waitingId', data.waitingId);
        if (previewTrack) { previewTrack.stop(); setPreviewTrack(null); }
      } else {
        setConnectionData(data);
        if (previewTrack) { previewTrack.stop(); setPreviewTrack(null); }
        setIsInCall(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to join meeting');
    } finally {
      setJoining(false);
    }
  };

  // Countdown timer — starts when entering call with a time limit
  React.useEffect(() => {
    if (!isInCall || !connectionData?.remainingMinutes) return;
    const maxMins = connectionData.maxMinutes;
    if (maxMins === -1) return; // unlimited

    let seconds = connectionData.remainingMinutes * 60;
    setRemainingSeconds(seconds);

    const interval = setInterval(() => {
      seconds -= 1;
      setRemainingSeconds(seconds);
      if (seconds <= 0) {
        clearInterval(interval);
        // Token will expire and LiveKit will disconnect automatically
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isInCall, connectionData]);

  const isHost = user && meetingInfo?.host_user_id === user._id;

  const handleLeave = () => {
    setIsInCall(false);
    setConnectionData(null);
    setChatOpen(false);
  };

  const handleAdmitted = (tokenData) => {
    setWaitingId(null);
    sessionStorage.removeItem('pf_waitingId');
    setConnectionData(tokenData);
    setIsInCall(true);
  };

  const handleRejected = () => {
    setWaitingId(null);
    sessionStorage.removeItem('pf_waitingId');
  };

  // Loading state
  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state (meeting not found, cancelled, ended, etc.)
  if (error && !meetingInfo) {
    const isEnded = error.includes('ended');
    const isCancelled = error.includes('cancelled');
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Globe className="h-8 w-8 text-primary" />
            <span className="font-display font-bold text-xl">PocketFiler</span>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center py-12">
              {isEnded ? (
                <>
                  <Video className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="font-medium text-lg">{t("meeting.ended")}</p>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    The host has ended this meeting. If you need to reconnect, ask the host to start a new meeting.
                  </p>
                </>
              ) : isCancelled ? (
                <>
                  <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
                  <p className="font-medium text-lg">{t("meeting.cancelled")}</p>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    This meeting has been cancelled by the host.
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                  <p className="font-medium text-lg">{t("meeting.notFound")}</p>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    This meeting link may be invalid or expired. Check with the host for an updated link.
                  </p>
                </>
              )}
              <Button variant="outline" className="mt-6" onClick={() => navigate('/')}>
                Go to PocketFiler
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // In-call view (full screen)
  if (isInCall && connectionData) {
    return (
      <div className="h-screen w-screen bg-background flex flex-col overflow-hidden">
        <RecordingConsentBanner
          isRecording={recordingStatus === 'recording'}
          isHost={isHost}
        />
        <LiveKitRoom
          serverUrl={connectionData.wsUrl}
          token={connectionData.token}
          connect={true}
          video={true}
          audio={true}
          onDisconnected={handleLeave}
          data-lk-theme="default"
          className="h-full w-full min-h-0 flex flex-col overflow-hidden"
          style={{ height: '100%', '--lk-bg': 'transparent' }}
        >
          {/* Use LiveKit's built-in components for a clean meeting experience */}
          <div className="flex flex-col h-full w-full">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b bg-background/95 shrink-0">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">{meetingInfo?.title}</span>
              </div>

              <div className="flex items-center gap-3">
                {meetingInfo?.allow_recording && (
                  <MeetingRecordingControls
                    meetingId={connectionData?.meetingId || meetingInfo?._id}
                    isHost={isHost}
                    onStatusChange={setRecordingStatus}
                  />
                )}
                <InCallBackgroundToggle />
                <WaitingRoomPanel
                  meetingId={connectionData?.meetingId || meetingInfo?._id}
                  isHost={isHost}
                />
              {remainingSeconds !== null && connectionData?.maxMinutes !== -1 && (
                <div className={`flex items-center gap-1 text-xs font-mono px-2 py-1 rounded ${
                  remainingSeconds <= 120 ? 'bg-red-100 text-red-700 animate-pulse' : 
                  remainingSeconds <= 300 ? 'bg-yellow-100 text-yellow-700' : 
                  'bg-muted text-muted-foreground'
                }`}>
                  <Clock className="h-3 w-3" />
                  {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
                </div>
              )}
              <Button variant="destructive" size="sm" onClick={handleLeave}>
                Leave Meeting
              </Button>
              </div>
            </div>

            {/* Video area — use the existing CustomVideoConference if available */}
            <div className="flex-1 min-h-0 overflow-hidden bg-black/5">
              {/* Import the inner conference component */}
              <MeetingVideoLayout
                chatOpen={chatOpen}
                setChatOpen={setChatOpen}
                onLeave={handleLeave}
              />
            </div>
          </div>
          <RoomAudioRenderer />
          <TranscriptForwarder meetingId={connectionData?.meetingId || meetingInfo?._id} />
        </LiveKitRoom>
      </div>
    );
  }

  // Waiting room view
  if (waitingId && !isInCall) {
    return (
      <WaitingRoom
        waitingId={waitingId}
        meetingInfo={meetingInfo}
        onAdmitted={handleAdmitted}
        onRejected={handleRejected}
      />
    );
  }

  // Pre-join lobby
  const scheduledStr = meetingInfo?.scheduled_at
    ? new Date(meetingInfo.scheduled_at).toLocaleString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
      })
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Globe className="h-8 w-8 text-primary" />
          <span className="font-display font-bold text-xl">PocketFiler</span>
        </div>

        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto p-3 rounded-full bg-primary/10 w-fit mb-3">
              <Video className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl">{meetingInfo?.title}</CardTitle>
            <CardDescription>
              Hosted by {meetingInfo?.host_name || 'PocketFiler User'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Meeting details */}
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              {scheduledStr && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {scheduledStr}
                </span>
              )}
              {meetingInfo?.duration_minutes && (
                <span>{meetingInfo.duration_minutes} min</span>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Camera preview with virtual background */}
            {previewTrack && (
              <div className="space-y-3">
                <video
                  ref={(el) => {
                    if (el && previewTrack) {
                      previewTrack.attach(el);
                    }
                  }}
                  autoPlay
                  muted
                  playsInline
                  className="rounded-lg w-full aspect-video bg-black object-cover -scale-x-100"
                />
                <BackgroundSelector
                  selectedId={selectedBg?.id}
                  onSelect={handleBgSelect}
                />
              </div>
            )}

            {/* If logged in, show quick join */}
            {user && token ? (
              <div className="space-y-3 pt-2">
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-sm text-muted-foreground">{t("meeting.joiningAs")}</p>
                  <p className="font-medium">{user.name || user.email}</p>
                </div>
                <Button
                  className="w-full h-12 text-base"
                  onClick={joinAsUser}
                  disabled={joining}
                >
                  {joining ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Connecting...</>
                  ) : (
                    <><Video className="h-5 w-5 mr-2" /> Join Meeting</>
                  )}
                </Button>
              </div>
            ) : (
              /* Guest join form */
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label htmlFor="guestName">{t("meeting.yourName")}</Label>
                  <Input
                    id="guestName"
                    placeholder="Enter your name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && joinAsGuest()}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="guestEmail">{t("meeting.emailOptional")}</Label>
                  <Input
                    id="guestEmail"
                    type="email"
                    placeholder="you@example.com"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && joinAsGuest()}
                  />
                </div>
                <Button
                  className="w-full h-12 text-base"
                  onClick={joinAsGuest}
                  disabled={joining || !guestName.trim()}
                >
                  {joining ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Connecting...</>
                  ) : (
                    <><Video className="h-5 w-5 mr-2" /> Join as Guest</>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  No account required. Your name will be visible to other participants.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Forwards LiveKit transcription segments to backend for AI summary
function TranscriptForwarder({ meetingId }) {
  const room = useRoomContext();

  useEffect(() => {
    if (!room || !meetingId) return;

    const handleTranscription = (segments, participant) => {
      for (const seg of segments) {
        const text = seg.text || seg.final || '';
        if (!text.trim()) continue;

        // Fire and forget - don't block the UI
        fetch(`${API_URL}/api/webhooks/livekit/transcript`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            meeting_id: meetingId,
            speaker: participant?.name || participant?.identity || 'Unknown',
            text: text.trim(),
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {}); // Silent fail - transcript is best-effort
      }
    };

    // LiveKit emits transcription events on the room
    room.on('transcriptionReceived', handleTranscription);

    return () => {
      room.off('transcriptionReceived', handleTranscription);
    };
  }, [room, meetingId]);

  return null; // Invisible component
}

// Meeting video layout using LiveKit's built-in VideoConference component
function MeetingVideoLayout() {
  return (
    <div className="h-full w-full">
      <VideoConference />
    </div>
  );
}
